import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { registerLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import {
  getAchatsEntityPrefix,
  isAchatsAutoReferenceEnabled,
} from './achatsConfigDefaults';
import { applyColumnFilters } from '../../shared/utils/columnHeaderFilters';
import { Tag, Trash2 } from 'lucide-react';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import {
  sliceQawaemStats,
  QAWAEM_KPI_GRID_CLASS,
} from '../../products/saudi/components/QawaemErpListPageShell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';

export { generateEntityReference, getEntityPrefix };

let achatsDatePickerLocalesReady = false;

/** Register react-datepicker locales for achats pages (Arabic-first). */
export function ensureAchatsDatePickerLocales() {
  if (achatsDatePickerLocalesReady) return;
  registerLocale('ar', arLocale);
  registerLocale('ar-SA', arLocale);
  if (!isSaudiBrand()) {
    void import('date-fns/locale/fr').then((mod) => registerLocale('fr', mod.default));
  }
  achatsDatePickerLocalesReady = true;
}

/** Map achats config defaultView → list viewMode (`table` | `cards`). */
export function resolveAchatsViewMode(defaultView) {
  return defaultView === 'dashboard' ? 'cards' : 'table';
}

/** Cap list rows to configured itemsPerPage. */
export function sliceAchatsListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

/** Live viewMode synced from achats config defaultView. */
export function useAchatsListViewMode(achatsConfig) {
  const defaultView = achatsConfig?.defaultView;
  const [viewMode, setViewMode] = useState(() => resolveAchatsViewMode(defaultView));
  useEffect(() => {
    setViewMode(resolveAchatsViewMode(defaultView));
  }, [defaultView]);
  return [viewMode, setViewMode];
}

/** Column-filtered list + itemsPerPage slice for table/card display. */
export function useAchatsListDisplay(baseList, colFilters, columnFilterRules, itemsPerPage) {
  const filtered = useMemo(
    () => applyColumnFilters(baseList, colFilters, columnFilterRules),
    [baseList, colFilters, columnFilterRules],
  );
  const displayed = useMemo(
    () => sliceAchatsListItems(filtered, itemsPerPage),
    [filtered, itemsPerPage],
  );
  return { filtered, displayed };
}

/** Settings-shaped object for prefix/approval helpers from live achats config. */
export function achatsSettingsFromConfig(achatsConfig) {
  return { achats: achatsConfig };
}

/** Next رمز for achats CRUD — uses achats config prefixes when available. */
export function resolveAchatsEntityReference(settings, prefixKey, fallbackPrefix, records, getRef, currentValue) {
  const trimmed = String(currentValue ?? '').trim();
  if (trimmed) return trimmed;
  if (!isAchatsAutoReferenceEnabled(settings, prefixKey)) return '';
  const prefix = getAchatsEntityPrefix(settings, prefixKey, fallbackPrefix);
  return generateEntityReference(prefix, records, getRef);
}

/** Shown when a submodule is turned off in إعدادات المشتريات. */
export function AchatsConfigNotice({ message }) {
  if (!message) return null;
  return (
    <div className="mb-4 rounded-2xl border border-amber-500/35 bg-amber-500/12 px-4 py-3 text-amber-100 text-sm text-right leading-relaxed">
      {message}
    </div>
  );
}

/** True when supplier-catalog stock is at or below reorder threshold. */
export function isAchatsCatalogLowStock(record) {
  const stock = parseInt(record?.stock_quantity, 10) || 0;
  const reorder = parseInt(record?.reorder_level, 10) || 0;
  return reorder > 0 && stock <= reorder;
}

export {
  unwrapAchatsApiList,
  fetchAchatsSuppliers,
  fetchAchatsProducts,
  fetchAchatsCatalog,
  fetchAchatsWarehouses,
  normalizeAchatsCatalogItem,
  findAchatsCatalogItem,
  countAchatsLowStockCatalog,
  buildAchatsProductSelectOptions,
  mapAchatsProductSelection,
} from './achatsCrossModuleData';

/** ISO date string offset by lead-time days from a base date. */
export function addLeadTimeDaysToDate(baseDate, leadDays) {
  const days = parseInt(leadDays, 10) || 0;
  if (!days) return '';
  const d = new Date(baseDate || new Date());
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export {
  AppNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
};

export { sliceQawaemStats, QAWAEM_KPI_GRID_CLASS };

/** Bottom toast matching Products.js / facturation CRUD pages. */
export function AchatsCrudNotificationToast({ notifyToast, onDismiss }) {
  return (
    <AppNotificationToast
      visible={notifyToast.visible}
      message={notifyToast.message}
      messageHtml={notifyToast.messageHtml}
      variant={notifyToast.variant}
      status={notifyToast.status}
      showActions={notifyToast.showActions}
      topLabel={notifyToast.topLabel}
      onDismiss={onDismiss}
      placement="bottom"
    />
  );
}

/** Tabbed create/edit modal layout (Products.js / ApprobationsCrudShell pattern). */
export const ACHATS_FORM_MODAL_SHELL = '-m-6 flex flex-col min-h-[320px]';
export const ACHATS_FORM_TAB_PANEL = 'overflow-y-auto p-6 flex-1 min-h-0';
export const ACHATS_FORM_GRID = 'grid grid-cols-1 md:grid-cols-3 gap-4';

export function achatsFormTabBarClass(isLight) {
  return `shrink-0 px-6 pt-3 border-b ${isLight ? 'border-gray-200' : 'border-white/10'}`;
}

export function achatsFormTabButtonClass(isLight, active) {
  return `inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
    active
      ? (isLight
        ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px'
        : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px')
      : (isLight
        ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
        : 'text-white/55 hover:text-white hover:bg-white/5')
  }`;
}

/** Theme-aware class tokens for achats CRUD modal form/view content. */
export function getAchatsModalClasses(cfgTheme) {
  const isLight = cfgTheme.light;
  return {
    isLight,
    section: isLight
      ? 'rounded-xl border border-slate-200 bg-white overflow-hidden'
      : 'bg-white/5 rounded-xl border border-white/20 overflow-hidden',
    sectionBtn: isLight
      ? 'w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors'
      : 'w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors',
    sectionTitle: isLight ? 'text-lg font-semibold text-slate-900' : 'text-lg font-semibold text-white',
    sectionChevron: isLight ? 'text-slate-400' : 'text-white/60',
    viewSection: isLight
      ? 'bg-slate-50 rounded-xl p-5 border border-slate-200'
      : 'bg-white/5 rounded-xl p-5 border border-white/20',
    viewSectionTitle: isLight
      ? 'text-slate-900 font-semibold mb-4 flex items-center gap-2'
      : 'text-white font-semibold mb-4 flex items-center gap-2',
    viewLabel: isLight ? 'text-slate-500 text-sm mb-1' : 'text-white/60 text-sm mb-1',
    viewValue: isLight ? 'text-slate-900 font-medium' : 'text-white font-medium',
    viewValueLg: isLight ? 'text-slate-900 font-medium text-lg' : 'text-white font-medium text-lg',
    viewRow: isLight
      ? 'flex justify-between gap-4 border-b border-slate-100 pb-2'
      : 'flex justify-between gap-4 border-b border-white/5 pb-2',
    viewRowLabel: isLight ? 'text-sm shrink-0 text-slate-500' : 'text-sm shrink-0 text-white/50',
    lineCard: isLight
      ? 'bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3'
      : 'bg-white/5 rounded-xl p-4 border border-white/10 space-y-3',
    lineLabel: isLight ? 'text-slate-600 text-sm font-medium' : 'text-white/70 text-sm font-medium',
    lineFieldLabel: isLight ? 'block text-slate-500 text-xs mb-1' : 'block w-full text-white/60 text-xs mb-1 font-medium text-right',
    addLineBtn: isLight
      ? 'w-full py-2 border border-dashed border-slate-300 rounded-xl text-slate-600 hover:text-slate-900 hover:border-slate-400 transition-all flex items-center justify-center gap-2'
      : 'w-full py-2 border border-dashed border-white/20 rounded-xl text-white/70 hover:text-white hover:border-white/40 transition-all flex items-center justify-center gap-2',
    inputSm: isLight
      ? 'w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
      : 'w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
    readOnly: isLight
      ? 'w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-slate-600 font-mono'
      : 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/70 font-mono',
  };
}

/** Hook: module theme + achats modal class tokens. */
export function useAchatsModalTheme() {
  const cfgTheme = useModuleConfigTheme();
  return { cfgTheme, modal: getAchatsModalClasses(cfgTheme) };
}

/** Map achats stat config to FacturationListPageLayout stats (max 4). */
export function mapAchatsStatCards(entries = [], max = 4) {
  return sliceQawaemStats(entries, max).map((stat, i) => ({
    key: stat.key || stat.label || stat.title || i,
    label: stat.label || stat.title,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
    color: stat.color,
    className: stat.className,
  }));
}

/** Build column picker defs from { key, labelKey? } + label fn `p`. */
export function buildAchatsColumnDefs(columns, p, columnVisibility = null) {
  return ensureStatusBeforeActions(columns).map((col) => ({
    key: col.key,
    label: col.labelKey ? p(col.labelKey) : col.label,
    checked: col.key === 'actions'
      ? true
      : columnVisibility
        ? columnVisibility[col.key] !== false
        : col.checked !== false,
    required: col.required || col.key === 'actions',
  }));
}

/** Column visibility state for achats list tables. */
export function useAchatsColumnVisibility(columnKeys = []) {
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(columnKeys.filter((k) => k !== 'actions').map((key) => [key, true])),
  );
  const handleToggleColumn = useCallback((key) => {
    if (key === 'actions') return;
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const isColumnVisible = useCallback(
    (key) => key === 'actions' || columnVisibility[key] !== false,
    [columnVisibility],
  );
  return { columnVisibility, handleToggleColumn, isColumnVisible };
}

/** Visible data columns count + checkbox + actions for colSpan. */
export function achatsTableColSpan(qawaemColumns) {
  const visible = qawaemColumns.filter((c) => c.key !== 'actions' && c.checked !== false).length;
  return 1 + visible + 1;
}

/** Period dropdown labels from page `p()` helper. */
export function buildAchatsPeriodLabels(p, t) {
  return {
    label: p('period', { defaultValue: t('common.period', { defaultValue: 'الفترة' }) }),
    startLabel: p('start_date', { defaultValue: t('common.start_date', { defaultValue: 'تاريخ البداية' }) }),
    endLabel: p('end_date', { defaultValue: t('common.end_date', { defaultValue: 'تاريخ النهاية' }) }),
    resetLabel: p('reset', { defaultValue: t('common.reset', { defaultValue: 'إعادة تعيين' }) }),
    applyLabel: p('apply', { defaultValue: t('common.apply', { defaultValue: 'تطبيق' }) }),
    selectPlaceholder: p('select', { defaultValue: t('common.select', { defaultValue: 'اختر' }) }),
  };
}

/** Secondary status filter for title row (الفترة · status · الأعمدة · search). */
export function buildAchatsSecondaryFilter({
  value,
  onChange,
  options,
  filterAllValue = 'all',
  filterAllLabel,
  icon = Tag,
}) {
  const normalized = !value || value === filterAllValue ? filterAllValue : value;
  const current = options.find((o) => o.value === normalized);
  return {
    label: current?.label || filterAllLabel || options[0]?.label,
    value: normalized,
    options,
    onChange: (val) => onChange(val === filterAllValue ? filterAllValue : val),
    icon,
  };
}

/** TableColumnHeaderFilter styling for achats glass tables */
export const ACHATS_COL_HEADER_TH = 'text-white font-semibold';

/** QawaemTableHeaderActionsCell class matching Products.js */
export const ACHATS_TABLE_ACTIONS_TH_CLASS =
  'px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle';

/** Row selection state for QawaemTableCheckbox header (filtered list). */
export function achatsRowSelectionState(selectedIds, filteredRows) {
  const ids = filteredRows.map((row) => row.id);
  const selectedInView = ids.filter((id) => selectedIds.includes(id));
  return {
    allRowsSelected: ids.length > 0 && selectedInView.length === ids.length,
    someRowsSelected: selectedInView.length > 0 && selectedInView.length < ids.length,
  };
}

/** Hook: row selection after filtered list is defined. */
export function useAchatsRowSelection(selectedIds, filteredRows) {
  return useMemo(
    () => achatsRowSelectionState(selectedIds, filteredRows),
    [selectedIds, filteredRows],
  );
}

/** Title-row bulk delete control for FacturationListPageLayout.filterActions */
export function buildAchatsBulkDeleteFilterAction(selectedCount, onDelete, labels = {}) {
  if (!selectedCount) return null;
  const { deleteLabel, title } = labels;
  return (
    <button
      type="button"
      onClick={onDelete}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
      title={title}
    >
      <Trash2 size={16} />
      <span>{deleteLabel}</span>
    </button>
  );
}

/** Inline bulk delete bar for legacy/custom achats list chrome (non-layout pages). */
export function AchatsBulkDeleteBar({ selectedCount, onDelete, deleteLabel, className = 'px-4 pt-4 flex justify-end' }) {
  if (!selectedCount) return null;
  return (
    <div className={className}>
      <button
        type="button"
        onClick={onDelete}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium"
      >
        <Trash2 size={16} />
        <span>{deleteLabel}</span>
      </button>
    </div>
  );
}

/** Build props for TableColumnHeaderFilter on achats list pages */
export function achatsColHeaderProps({
  columnKey,
  label,
  colFilters,
  setColFilter,
  editingKey,
  setEditingKey,
  kind = 'text',
  options = [],
  align = 'start',
}) {
  return {
    columnKey,
    label,
    kind,
    value: colFilters[columnKey] ?? '',
    onChange: (v) => setColFilter(columnKey, v),
    options,
    editingKey,
    onEditingKeyChange: setEditingKey,
    thClassName: ACHATS_COL_HEADER_TH,
    align,
  };
}

const achatsImportExportNoop = () => {};

/** Import/export toolbar props — pass `handlers` when the page supports I/O. */
export function buildAchatsImportExportProps({
  showImportDropdown,
  setShowImportDropdown,
  showExportDropdown,
  setShowExportDropdown,
  importDropdownRef,
  exportDropdownRef,
  p,
  t,
  importLabel,
  exportLabel,
  generatingLabel,
  isGeneratingPDF = false,
  handlers = {},
}) {
  return {
    importOpen: showImportDropdown,
    exportOpen: showExportDropdown,
    onToggleImport: () => {
      setShowImportDropdown(!showImportDropdown);
      setShowExportDropdown(false);
    },
    onToggleExport: () => {
      setShowExportDropdown(!showExportDropdown);
      setShowImportDropdown(false);
    },
    importRef: importDropdownRef,
    exportRef: exportDropdownRef,
    importLabel: importLabel || p?.('import', { defaultValue: t?.('common.import', { defaultValue: 'استيراد' }) }),
    exportLabel: exportLabel || t?.('common.export', { defaultValue: 'تصدير' }),
    generatingLabel: generatingLabel || p?.('generating', { defaultValue: t?.('common.loading', { defaultValue: 'جاري الإنشاء...' }) }),
    isGeneratingPDF,
    onImportCSV: handlers.onImportCSV || handlers.onImport || achatsImportExportNoop,
    onImportExcel: handlers.onImportExcel || handlers.onImport || achatsImportExportNoop,
    onImportJSON: handlers.onImportJSON || achatsImportExportNoop,
    onExportPDF: handlers.onExportPDF || achatsImportExportNoop,
    onExportExcel: handlers.onExportExcel || achatsImportExportNoop,
    onExportCSV: handlers.onExportCSV || handlers.onExport || achatsImportExportNoop,
    onExportJSON: handlers.onExportJSON || achatsImportExportNoop,
    showJsonImportExport: handlers.showJsonImportExport ?? false,
  };
}
